"use client";

import { useEffect, useMemo, useRef } from "react";
import { Button, Heading, ImageWrapper } from "@farm-junction/design-library";
import ImagePageFilters from "./ImagePageFilters";
import ImageGallerySectionLib from "@/component/ImageGallerySectionLib";
import {
    getActiveGallerySectionId,
    getGalleryCategoryCounts,
    scrollToGalleryFilterBar,
    scrollToGallerySection,
} from "./galleryData";
import { useImagePageMedia } from "./ImagePageMedia";
import type {
    GalleryCategoryId,
    GalleryColour,
    ImageGalleryItem,
    ImagePageGalleryData,
} from "./types";

type ImagePageGalleryProps = {
    data: ImagePageGalleryData;
    onImageClick?: (image: ImageGalleryItem) => void;
    onColourClick?: (colour: GalleryColour) => void;
};

function ColourCard({
    colour,
    onClick,
}: {
    colour: GalleryColour;
    onClick?: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={colour.title}
            title={colour.title}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white text-left"
        >
            <div className="relative aspect-[16/10] bg-[#F4F4F4]">
                <ImageWrapper
                    src={colour.image}
                    alt={colour.alt}
                    title={colour.title}
                    width={320}
                    height={180}
                    className="h-full w-full object-cover"
                />
            </div>
            <div className="flex items-center gap-2 px-3 py-2.5">
                <span
                    className="h-3.5 w-3.5 shrink-0 rounded-full border border-gray-200"
                    style={{ backgroundColor: colour.hex }}
                />
                <span className="truncate text-sm font-medium text-gray-800">{colour.name}</span>
            </div>
        </button>
    );
}

const toLibImages = (images: ImageGalleryItem[]) =>
    images.map((image) => ({
        id: image.id,
        src: image.src,
        alt: image.alt,
        title: image.title,
        label: image.label,
    }));

export default function ImagePageGallery({ data, onImageClick, onColourClick }: ImagePageGalleryProps) {
    const {
        openLightbox,
        activeCategory,
        activeSubcategory,
        setActiveCategory,
        setActiveSubcategory,
    } = useImagePageMedia();
    const counts = useMemo(() => getGalleryCategoryCounts(data), [data]);
    const ignoreScrollSpyRef = useRef(false);
    const ignoreScrollSpyTimerRef = useRef<number>(0);
    const activeSubcategoryRef = useRef(activeSubcategory);
    activeSubcategoryRef.current = activeSubcategory;

    const visibleSections = useMemo(() => {
        if (activeCategory === "colour") return [];
        return data.sections
            .filter((section) => activeCategory === "all" || section.category === activeCategory)
            .filter((section) => section.images.length > 0);
    }, [activeCategory, data.sections]);
    const visibleSectionIds = useMemo(
        () => visibleSections.map((section) => section.id),
        [visibleSections],
    );

    const pauseScrollSpy = (durationMs = 800) => {
        ignoreScrollSpyRef.current = true;
        window.clearTimeout(ignoreScrollSpyTimerRef.current);
        const resume = () => {
            ignoreScrollSpyRef.current = false;
            window.removeEventListener("scrollend", resume);
        };
        window.addEventListener("scrollend", resume, { once: true });
        ignoreScrollSpyTimerRef.current = window.setTimeout(resume, durationMs);
    };

    useEffect(() => {
        if (activeCategory === "colour") return;

        const syncTabFromScroll = () => {
            if (ignoreScrollSpyRef.current) return;
            const nextSubcategory = getActiveGallerySectionId(visibleSectionIds);
            if (nextSubcategory === activeSubcategoryRef.current) return;
            setActiveSubcategory(nextSubcategory);
        };

        window.addEventListener("scroll", syncTabFromScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", syncTabFromScroll);
            window.clearTimeout(ignoreScrollSpyTimerRef.current);
        };
    }, [activeCategory, setActiveSubcategory, visibleSectionIds]);

    const showColours = activeCategory === "all" || activeCategory === "colour";
    const visibleColours = activeCategory === "colour" ? data.colours : data.colours.slice(0, 6);
    const categoryLabelCount = data.categories.filter((category) => category.id !== "all").length;

    const hasSubcategoryPills =
        activeCategory !== "colour" &&
        data.subcategories.filter(
            (item) => item.id === "all" || activeCategory === "all" || item.category === activeCategory,
        ).length > 1;

    // While the sticky sub-category bar occupies the secondary nav's slot, hide the nav so it
    // doesn't show through behind the bar. It comes back as soon as the bar is no longer there
    // (before the gallery is reached, or once the bar has scrolled out with the section).
    useEffect(() => {
        if (!hasSubcategoryPills) return;

        const nav = document.querySelector<HTMLElement>("[data-secondary-nav]");
        if (!nav) return;

        let ticking = false;
        const sync = () => {
            ticking = false;
            const bar = document.querySelector<HTMLElement>("[data-gallery-filter-bar]");
            if (!bar) {
                nav.style.visibility = "";
                return;
            }
            const stickyTop = Number.parseFloat(window.getComputedStyle(bar).top) || 0;
            const rect = bar.getBoundingClientRect();
            const covering = rect.top <= stickyTop + 1 && rect.bottom > stickyTop;
            nav.style.visibility = covering ? "hidden" : "";
        };
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(sync);
        };

        sync();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            nav.style.visibility = "";
        };
    }, [hasSubcategoryPills]);

    const handleCategoryChange = (id: GalleryCategoryId) => {
        pauseScrollSpy(1200);
        setActiveCategory(id);
        setActiveSubcategory("all");
        window.requestAnimationFrame(() => {
            if (id === "colour") {
                scrollToGalleryFilterBar();
                return;
            }
            scrollToGallerySection("all");
        });
    };

    const handleSubcategoryChange = (id: string) => {
        pauseScrollSpy();
        setActiveSubcategory(id);
        scrollToGallerySection(id);
    };

    if (!data.fromGalleryApi) return null;

    return (
        <section className="mt-2">
            <div className="mb-4 min-h-[56px] md:mb-6">
                <Heading variant="h2" level={2} className="!mb-1 text-xl font-bold text-gray-900 md:text-[28px] md:leading-8">
                    {data.carName} Pictures - Exterior, Interior & Colour Photos
                </Heading>
                <p className="mt-1 text-sm text-gray-500">
                    {data.imageCount} official images
                    {categoryLabelCount > 0
                        ? ` across ${categoryLabelCount} ${categoryLabelCount === 1 ? "category" : "categories"}`
                        : ""}
                </p>
            </div>

            {/* Category tabs scroll away normally; only the sub-category pills below stick. */}
            <div id="gallery-category-tabs" className="pt-1 pb-3">
                <ImagePageFilters
                    variant="page"
                    show="categories"
                    categories={data.categories}
                    subcategories={data.subcategories}
                    counts={counts}
                    activeCategory={activeCategory}
                    activeSubcategory={activeSubcategory}
                    onCategoryChange={handleCategoryChange}
                    onSubcategoryChange={handleSubcategoryChange}
                />
            </div>

            {hasSubcategoryPills && (
                // Sticks in the secondary nav's slot (same top offset, stacked above it) and is
                // released together with this section, so the nav is back once the gallery ends.
                <div
                    data-gallery-filter-bar
                    className="sticky top-[73px] z-99 -mx-5 bg-white px-5 py-2 shadow-sm md:top-[85px]"
                >
                    <ImagePageFilters
                        variant="page"
                        show="subcategories"
                        categories={data.categories}
                        subcategories={data.subcategories}
                        counts={counts}
                        activeCategory={activeCategory}
                        activeSubcategory={activeSubcategory}
                        onCategoryChange={handleCategoryChange}
                        onSubcategoryChange={handleSubcategoryChange}
                    />
                </div>
            )}

            {activeCategory !== "colour" && (
                <div id="gallery-sections-start" className="mt-2 space-y-8 md:space-y-12">
                    {visibleSections.length === 0 && (
                        <p className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                            No images found in this category.
                        </p>
                    )}

                    {visibleSections.map((section) => (
                        <div
                            key={section.id}
                            id={`gallery-section-${section.id}`}
                        >
                            <ImageGallerySectionLib
                                data={{
                                    title: section.title,
                                    description: section.description,
                                    viewAllLabel: "View All Images",
                                    images: toLibImages(section.images),
                                }}
                                onImageClick={(_, image) => {
                                    const matched = data.images.find((item) => String(item.id) === String(image.id)) || section.images[0];
                                    if (onImageClick) onImageClick(matched);
                                    else openLightbox(matched?.id, section.category, section.id);
                                }}
                                onViewAllClick={() => {
                                    const image = section.images[0] || data.images[0];
                                    if (onImageClick) onImageClick(image);
                                    else openLightbox(image?.id, section.category, section.id);
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}

            {showColours && data.colours.length > 0 && (
                <div className={activeCategory === "colour" ? "mt-4 md:mt-6" : "mt-10 md:mt-14"}>
                    <div className="mb-4 flex items-end justify-between gap-3">
                        <div>
                            <Heading variant="h3" level={3} className="!mb-1 text-lg font-bold text-gray-900 md:text-2xl">
                                Colors
                            </Heading>
                            <p className="mt-1 text-sm text-gray-500">
                                {visibleColours.length === data.colours.length
                                    ? `${data.colours.length} body shades shown in studio light.`
                                    : `${visibleColours.length} of ${data.colours.length} body shades shown in studio light.`}
                            </p>
                        </div>
                        {data.colours.length > 6 && activeCategory !== "colour" && (
                            <Button
                                variant="secondary"
                                title={`View all ${data.carName} colours`}
                                aria-label={`View all ${data.carName} colours`}
                                className="mb-0 hidden rounded-[12px] border border-[#EDF0F3] bg-[#FBFCFD] px-5 py-2.5 text-[12px] text-[#343941] md:inline-flex"
                                onClick={() => {
                                    setActiveCategory("colour");
                                    setActiveSubcategory("all");
                                }}
                            >
                                View All Colors
                            </Button>
                        )}
                    </div>

                    <div className="grid min-h-[160px] grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
                        {visibleColours.map((colour) => (
                            <ColourCard
                                key={colour.id}
                                colour={colour}
                                onClick={() => {
                                    if (onColourClick) onColourClick(colour);
                                    else openLightbox(colour.id, "colour");
                                }}
                            />
                        ))}
                    </div>

                    {data.colours.length > 6 && activeCategory !== "colour" && (
                        <div className="mt-4 text-center md:hidden">
                            <Button
                                variant="secondary"
                                title={`View all ${data.carName} colours`}
                                aria-label={`View all ${data.carName} colours`}
                                className="inline-flex rounded-[12px] border border-[#EDF0F3] bg-[#FBFCFD] px-5 py-2.5 text-[12px] text-[#343941]"
                                onClick={() => {
                                    setActiveCategory("colour");
                                    setActiveSubcategory("all");
                                }}
                            >
                                View All Colors
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
